import React, { useState, useEffect, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Input } from './ui/input';
import { Loader2, Search, Replace } from 'lucide-react';
import { cn } from '../lib/utils';

const API = '/api';

/**
 * Each placeholder kind defines:
 *  - token     : exact placeholder string to scan for in JSON args (e.g. "<TASK_ID>")
 *  - label     : pretty button label
 *  - fetchUrl  : list endpoint
 *  - extract   : (data) => array of items
 *  - getId     : (item) => the id string to substitute in
 *  - getTitle  : (item) => human label
 *  - getMeta   : (item) => secondary line (optional)
 */
const PLACEHOLDERS = [
  {
    token: '<TASK_ID>',
    label: 'Pick a task',
    fetchUrl: `${API}/tasks?limit=25`,
    extract: (d) => d.tasks || d.items || (Array.isArray(d) ? d : []),
    getId: (t) => t.task_id,
    getTitle: (t) => t.task_title || t.title || t.task_id,
    getMeta: (t) => [t.task_status, t.task_priority != null ? `P${t.task_priority}` : null, t.task_delivery_date].filter(Boolean).join(' · '),
  },
  {
    token: '<BIGPICTURE_ID>',
    label: 'Pick a Big Picture item',
    fetchUrl: `${API}/bigpicture/items`,
    extract: (d) => d.items || d.bigpicture_items || (Array.isArray(d) ? d : []),
    getId: (b) => b.item_id || b.bigpicture_id,
    getTitle: (b) => b.title || b.item_id,
    getMeta: (b) => [b.item_type, b.target_date].filter(Boolean).join(' · '),
  },
  {
    token: '<THREAD_ID>',
    label: 'Pick a thread',
    fetchUrl: `${API}/switchboard/threads?limit=25`,
    extract: (d) => d.threads || d.items || (Array.isArray(d) ? d : []),
    getId: (t) => t.thread_id,
    getTitle: (t) => t.subject || t.preview || t.thread_id,
    getMeta: (t) => [t.last_message_from, t.message_count != null ? `${t.message_count} msgs` : null].filter(Boolean).join(' · '),
  },
  {
    token: '<CONTACT_ID>',
    label: 'Pick a contact',
    fetchUrl: `${API}/switchboard/contacts?limit=50`,
    extract: (d) => d.contacts || d.items || (Array.isArray(d) ? d : []),
    getId: (c) => c.contact_id,
    getTitle: (c) => c.display_name || c.name || c.contact_id,
    getMeta: (c) => c.email || c.phone || '',
  },
];

const PickerPopover = ({ kind, onPick }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null); // null = not fetched, [] = fetched empty
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open && items === null) {
      setLoading(true);
      fetch(kind.fetchUrl, { credentials: 'include' })
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(d => setItems(kind.extract(d) || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }
  }, [open, items, kind]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (!filter) return items;
    const f = filter.toLowerCase();
    return items.filter(i => {
      const t = (kind.getTitle(i) || '').toLowerCase();
      const m = (kind.getMeta(i) || '').toLowerCase();
      const id = (kind.getId(i) || '').toLowerCase();
      return t.includes(f) || m.includes(f) || id.includes(f);
    });
  }, [items, filter, kind]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-[11px] px-2 py-0.5 rounded-full border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60 transition flex items-center gap-1"
          data-testid={`placeholder-pick-${kind.token.replace(/[<>]/g, '')}`}
        >
          <Replace className="w-3 h-3" />
          {kind.label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b">
          <div className="flex items-center gap-1 px-2 py-1 rounded border bg-muted/30">
            <Search className="w-3 h-3 text-muted-foreground" />
            <Input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter..."
              className="h-6 text-[11px] border-0 bg-transparent shadow-none focus-visible:ring-0 px-1"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : (filtered || []).length === 0 ? (
            <div className="p-4 text-center text-[11px] text-muted-foreground">
              {items === null ? 'Loading...' : `No ${kind.token} matches`}
            </div>
          ) : (
            <div className="divide-y">
              {filtered.slice(0, 50).map((it, i) => {
                const id = kind.getId(it);
                if (!id) return null;
                return (
                  <button
                    key={`${id}-${i}`}
                    type="button"
                    onClick={() => { onPick(id); setOpen(false); setFilter(''); }}
                    className="w-full text-left px-3 py-2 hover:bg-muted/50 transition"
                    data-testid={`placeholder-pick-item-${id}`}
                  >
                    <p className="text-[11px] font-medium truncate">{kind.getTitle(it)}</p>
                    <p className="text-[10px] font-mono text-muted-foreground truncate">{id}</p>
                    {kind.getMeta(it) && (
                      <p className="text-[10px] text-muted-foreground truncate">{kind.getMeta(it)}</p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const PlaceholderPickerBar = ({ argsText, onArgsChange }) => {
  // Detect which placeholders appear in the current args text.
  const detected = useMemo(() =>
    PLACEHOLDERS.filter(k => argsText.includes(k.token)),
    [argsText]
  );
  if (detected.length === 0) return null;

  const replaceToken = (token, value) => {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    onArgsChange(argsText.replace(new RegExp(escaped, 'g'), value));
  };

  return (
    <div
      className="mb-2 p-2 rounded-lg border border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/30"
      data-testid="placeholder-picker-bar"
    >
      <p className="text-[10px] uppercase font-semibold text-blue-700 dark:text-blue-300 mb-1">
        Detected placeholders — click to fill with a real id
      </p>
      <div className="flex flex-wrap gap-1.5">
        {detected.map(kind => (
          <PickerPopover
            key={kind.token}
            kind={kind}
            onPick={(id) => replaceToken(kind.token, id)}
          />
        ))}
      </div>
    </div>
  );
};

export default PlaceholderPickerBar;
